// src/hazir-beton/src/pages/CarsafPompa.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  deleteField
} from "firebase/firestore";
import { createPortal } from "react-dom";
import { db, auth } from "../firebaseConfig";

const INOSEL_ID = "27517";

/* ================= HELPERS ================= */

const asText = (v) => String(v ?? "").trim();
const normKey = (v) => asText(v).replace(/\s+/g, "").toUpperCase();
const uniq = (arr) => [...new Set(arr)].filter(Boolean);

const safeNum = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const fmtTr = (v) => {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};



const formatTimeSmart = (v) => {
  const d = String(v ?? "").replace(/\D/g, "");
  if (!d) return "";
  const s = d.length >= 4 ? d.slice(-4) : d.padStart(4, "0");
  return `${s.slice(0, 2)}:${s.slice(2, 4)}`;
};

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const parseHHMM = (hhmm) => {
  const t = String(hhmm ?? "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

const minutesToHHMM = (mins) => {
  if (mins == null) return "";
  const m = Math.max(0, Math.round(mins));
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const calcSure = (baslangicSaat, bitisSaat) => {
  const s = parseHHMM(baslangicSaat);
  const e = parseHHMM(bitisSaat);
  if (s == null || e == null) return "";
  let d = e - s;
  if (d < 0) d += 24 * 60;
  return minutesToHHMM(d);
};

/* ================= 3-FIELD COMPAT (operatorAdi/operatorAdi2/operatorAdi3) ================= */

const listToFirestoreFields3 = (list) => {
  const arr = (list || []).map((x) => asText(x)).filter(Boolean);
  return { v1: arr[0] || "", v2: arr[1] || "", v3: arr[2] || "" };
};

const fromFirestoreFields3 = (a, a2, a3) => {
  const out = [];
  if (asText(a)) out.push(asText(a));
  if (asText(a2)) out.push(asText(a2));
  if (asText(a3)) out.push(asText(a3));
  return out;
};

/* ================= PERSONEL LIST (AUTOCOMPLETE) ================= */

// Aktif personel: giriş sayısı > çıkış sayısı
const isAktif = (tarih = {}) => {
  const gi = Object.keys(tarih).filter((k) => k.startsWith("giris")).length;
  const ci = Object.keys(tarih).filter((k) => k.startsWith("cikis")).length;
  return gi > ci;
};

// Sadece bu tesislerden personel göster
const HAZIR_BETON_TESISLER = ["SİNCAN", "GÜVERCİNLİK", "ASKİ İVEDİK", "ONKOLOJİ"];

const usePersonelList = () => {
  const [personeller, setPersoneller] = useState([]);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "personeller"));
      const list = snap.docs
        .map((d) => d.data())
        .filter((p) => {
          if (!p?.ad) return false;
          // Tesis filtresi — büyük/küçük harf duyarsız
          const tesis = (p.tesis || "").trim().toLocaleUpperCase("tr-TR");
          if (!HAZIR_BETON_TESISLER.includes(tesis)) return false;
          // Aktif personel filtresi
          return isAktif(p.tarih || {});
        })
        .map((p) => `${p.ad}${p.soyad ? " " + p.soyad : ""}`)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "tr"));
      setPersoneller(list);
    })();
  }, []);

  return personeller;
};

/* ================= FILTER POPUP (CarsafSevkiyat.js ile aynı mantık) ================= */

function FilterPopup({
  anchor,
  values,
  selected,
  onToggle,
  onSelectAll,
  onClear,
  onClose,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  if (!anchor) return null;

  const style = {
    position: "absolute",
    top: anchor.bottom + window.scrollY + 4,
    left: anchor.left + window.scrollX,
    background: "#fff",
    border: "1px solid #ccc",
    padding: 8,
    zIndex: 9999,
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    maxHeight: 260,
    overflowY: "auto",
    minWidth: 240,
  };

  return createPortal(
    <div ref={ref} style={style}>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <button type="button" onClick={onSelectAll}>
          Tümünü Seç
        </button>
        <button type="button" onClick={onClear}>
          Temizle
        </button>
      </div>

      {values.map((v) => (
<label key={v} style={{ display: "block" }}>
  <input
    type="checkbox"
    checked={selected.includes(v)}
    onChange={() => onToggle(v)}
  />{" "}
  {v || "(Boş)"}
</label>

      ))}
      <button type="button" onClick={onClose} style={{ marginTop: 6 }}>
        Kapat
      </button>
    </div>,
    document.body
  );
}

/* ================= MULTI INPUT CELL (same cell + plus button + datalist) ================= */

function DynamicListCell({
  values,
  onChange,
  datalistId,
  placeholder = "",
  addLabel = "+",
  disabled = false,
  onFocusFirst,
  onBlurFirst,
}) {
  const safeVals = Array.isArray(values) && values.length ? values : [""];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {safeVals.map((v, idx) => (
        <input
          key={idx}
          type="text"
          disabled={disabled}
          value={v ?? ""}
          placeholder={idx === 0 ? placeholder : ""}
          list={datalistId}
          onFocus={idx === 0 ? onFocusFirst : undefined}
          onBlur={idx === 0 ? onBlurFirst : undefined}
            onChange={(e) => {
            const next = [...safeVals];
            next[idx] = e.target.value;
            onChange(next);
          }}
        />
      ))}

      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange([...safeVals, ""])}
        style={{
          width: "fit-content",
          padding: "2px 8px",
          fontSize: 12,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {addLabel}
      </button>
    </div>
  );
}

/* ================= COMPONENT ================= */

export default function CarsafPompa() {
  const personeller = usePersonelList();

  const [filterDate, setFilterDate] = useState(yesterdayISO());
  const [saving, setSaving] = useState(false);
  

  // --- Tesis bazlı özet tablo (sağ üst) ---
  const [ozetRows, setOzetRows] = useState([]); // [{tesis,pompalar,mikserler,diger,toplam}]
  const [ozetTotals, setOzetTotals] = useState({ pompalar: 0, mikserler: 0, diger: 0, toplam: 0 });
  const ozetRef = useRef(null);
  const [ozetHeight, setOzetHeight] = useState(0);

const [loading, setLoading] = useState(false);

  // irsaliye bazlı ham kayıtlar
  const [docs, setDocs] = useState([]);

  // groupKey -> defaults (apply-to-all) : baslangicSaat/bitisSaat/operatorList/yardimciList
  const [groupState, setGroupState] = useState({});

  // docId -> row values + override map
  const [rowState, setRowState] = useState({});

  // excel like expand/collapse
  const [expanded, setExpanded] = useState({});

  // filters
  const [filters, setFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);

  // validation highlight
  const [badDocIds, setBadDocIds] = useState([]);
  const [badGroupKeys, setBadGroupKeys] = useState([]);
// lock (Model C): group bazlı kilit. Bir gruba/satıra tıklanınca o grubun tüm irsaliyeleri kilitlenir.
const [activeLockGroupKey, setActiveLockGroupKey] = useState(null);

const nowIsoLocal = () => {
  try {
    return new Date().toLocaleString("tr-TR");
  } catch {
    return String(new Date());
  }
};

const fmtLockTime = (ts) => {
  if (!ts) return "";
  try {
    const d = typeof ts?.toDate === "function" ? ts.toDate() : new Date(ts);
    return d.toLocaleString("tr-TR");
  } catch {
    return "";
  }
};

const getGroupLockInfoFromDocs = (gk) => {
  const g = groups.find((x) => x.groupKey === gk);
  if (!g) return { active: false, by: "", at: null };
  // herhangi bir satırda active lock varsa onu göster
  for (const r of g.rows) {
    const lk = r.lock || null;
    if (lk?.active) return { active: true, by: lk.by || "", at: lk.at || null };
  }
  return { active: false, by: "", at: null };
};

const isLockedByOther = (gk) => {
  const email = auth.currentUser?.email || "";
  const lk = getGroupLockInfoFromDocs(gk);
  return Boolean(lk.active && lk.by && lk.by !== email);
};

const lockTooltip = (gk) => {
  const lk = getGroupLockInfoFromDocs(gk);
  if (!lk?.active) return "";
  const t = fmtLockTime(lk.at);
  return `Kilitli: ${lk.by || "bilinmeyen kullanıcı"}${t ? " - " + t : ""} tarihinde açtı`;
};

const queryGroupDocs = async (gk) => {
  const g = groups.find((x) => x.groupKey === gk);
  if (!g) return [];
  const pompaPlaka = asText(g.pompaPlaka);
  const santiyeAdi = asText(g.santiyeAdi);
  const snap = await getDocs(
    query(
      collection(db, "hazirbeton_pompa_hakedis"),
      where("pompaFirmaId", "==", INOSEL_ID),
      where("tarihStr", "==", filterDate),
      where("pompaPlaka", "==", pompaPlaka),
      where("santiyeAdi", "==", santiyeAdi)
    )
  );
  return snap.docs.map((d) => ({ id: d.id, data: d.data() || {} }));
};

const setGroupLock = async (gk, active) => {
  const email = auth.currentUser?.email || "";

  const docsInGroup = await queryGroupDocs(gk);

  // aktif kilit başka kullanıcıdaysa engelle (Model C)
  if (active) {
    const other = docsInGroup.find((x) => x.data?.lock?.active && x.data?.lock?.by && x.data.lock.by !== email);
    if (other) {
      const t = fmtLockTime(other.data.lock.at);
      alert(`Bu grup şu anda kilitli: ${other.data.lock.by}${t ? " (" + t + ")" : ""}`);
      return false;
    }
  }

  const batch = writeBatch(db);
  for (const x of docsInGroup) {
    const ref = doc(db, "hazirbeton_pompa_hakedis", x.id);
    if (active) {
      batch.update(ref, {
        lock: {
          active: true,
          by: email,
          at: serverTimestamp(),
        },
      });
    } else {
      batch.update(ref, {
        lock: deleteField(),
      });
    }
  }
  await batch.commit();

  // local docs state güncelle (UI anında gri/gri değil görsün)
  setDocs((prev) =>
    prev.map((r) => {
      const belongs =
        `${normKey(r.pompaPlaka)}__${r.santiyeAdi}` === gk &&
        r.tarihStr === filterDate;
      if (!belongs) return r;
      return active
        ? { ...r, lock: { active: true, by: email, at: { toDate: () => new Date() } } }
        : { ...r, lock: null };
    })
  );

  return true;
};

const releaseActiveGroupLock = async () => {
  if (!activeLockGroupKey) return;
  try {
    await setGroupLock(activeLockGroupKey, false);
  } catch {
    // silent
  } finally {
    setActiveLockGroupKey(null);
  }
};

const acquireGroupLock = async (gk) => {
  // önce eski kilidi bırak
  if (activeLockGroupKey && activeLockGroupKey !== gk) {
    await releaseActiveGroupLock();
  }

  // zaten aynı grup kilitliyse yeniden deneme
  if (activeLockGroupKey === gk) return true;

  const ok = await setGroupLock(gk, true);
  if (ok) setActiveLockGroupKey(gk);
  return ok;
};

// sayfadan çıkarken / component unmount olurken kilidi bırak
useEffect(() => {
  return () => {
    if (activeLockGroupKey) {
      // fire-and-forget
      setGroupLock(activeLockGroupKey, false).catch(() => {});
    }
  };
  // eslint-disable-next-line
}, [activeLockGroupKey]);

  const [bomMap, setBomMap] = useState({}); // pompaPlaka (normalized) -> bomUzunlugu
  const spaceShortcutRef = useRef(false);

  /* -------- FETCH -------- */

  const fetchRows = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, "hazirbeton_pompa_hakedis"),
          where("deleted", "==", false),
          where("tarihStr", "==", filterDate),
          where("pompaFirmaId", "==", INOSEL_ID)
        )
      );

      const list = snap.docs.map((d) => {
        const r = d.data() || {};
        const operatorList = fromFirestoreFields3(
          r.operatorAdi,
          r.operatorAdi2,
          r.operatorAdi3
        );
        const yardimciList = fromFirestoreFields3(
          r.yagciAdi,
          r.yagciAdi2,
          r.yagciAdi3
        );

        return {
          _id: d.id,
          musteriAdi: r.musteriAdi || "",
          santiyeAdi: r.santiyeAdi || "",
          tarihStr: r.tarihStr || filterDate,
          irsaliyeNo: r.irsaliyeNo || "",
          gelenM3: r.m3 ?? "",
          mikserPlaka: r.mikserPlaka || "",
          pompaPlaka: r.pompaPlaka || "",
          baslangicSaat: r.baslangicSaat || "",
          bitisSaat: r.bitisSaat || "",
          fiiliM3: r.fiiliM3 ?? "",
          operatorList,
          yardimciList,
          tamamlandi: r.tamamlandi === true,
          lock: r.lock || null,
        };
      });

      setDocs(list);

      // ---- BOM BİLGİSİ: plates koleksiyonundan çek ----
      try {
        const platesSnap = await getDocs(collection(db, "plates"));
        const bm = {};
        platesSnap.docs.forEach((d) => {
          const p = d.data();
          if (p.plate) {
            const key = normKey(p.plate);
            bm[key] = p.bomUzunlugu ?? "";
          }
        });
        setBomMap(bm);
      } catch (e) { console.error("Bom verisi alınamadı:", e); }


      // ---- ÖZET TABLO (aynı tarih) ----
      try {
        const sumByTesis = (arr, amountFn) => {
          const m = new Map();
          for (const it of arr) {
            const t = asText(it?.tesis);
            if (!t) continue;
            const a = amountFn(it);
            m.set(t, (m.get(t) || 0) + (Number.isFinite(a) ? a : 0));
          }
          return m;
        };

        // 1) Pompalarımız: hazirbeton_pompa_hakedis (o tarihteki tüm dökümler)
        const pompaSnap = await getDocs(
          query(
            collection(db, "hazirbeton_pompa_hakedis"),
            where("deleted", "==", false),
            where("tarihStr", "==", filterDate),
            where("pompaFirmaId", "==", INOSEL_ID)
          )
        );
        const pompaDocs = pompaSnap.docs.map((d) => d.data() || {});
        const pompaByTesis = sumByTesis(pompaDocs, (x) => safeNum(x.fiiliM3 ?? x.m3 ?? x.gelenM3) ?? 0);

        // 2) Mikserlerimiz: hazirbeton_mikser_sevkiyat (o tarihteki tüm sevkiyatlar)
        const mikserSnap = await getDocs(
          query(
            collection(db, "hazirbeton_mikser_sevkiyat"),
            where("deleted", "==", false),
            where("tarihStr", "==", filterDate),
            where("mikserFirmaId", "==", INOSEL_ID)
          )
        );
        const mikserDocs = mikserSnap.docs.map((d) => d.data() || {});
        const mikserByTesis = sumByTesis(mikserDocs, (x) => safeNum(x.m3 ?? x.m) ?? 0);

        // 3) Diğer: hazirbeton_zone_uretim (mikser/pompa irsaliyeleri dışında kalanlar)
    
        const zoneSnap = await getDocs(
          query(
            collection(db, "hazirbeton_zone_uretim"),
            where("deleted", "==", false),
            where("tarihStr", "==", filterDate)
          )
        );
        const zoneDocs = zoneSnap.docs.map((d) => d.data() || {});
        const digerDocs = zoneDocs.filter(
          (x) =>
            String(x.mikserFirmaId ?? "") !== String(INOSEL_ID) &&
            String(x.pompaFirmaId ?? "") !== String(INOSEL_ID)
        );
        const digerByTesis = sumByTesis(
          digerDocs,
          (x) => safeNum(x.m3 ?? x.uretimM3 ?? x.miktar ?? x.toplamM3 ?? x.gelenM3) ?? 0
        );

        const tesisSet = new Set([
          ...Array.from(pompaByTesis.keys()),
          ...Array.from(mikserByTesis.keys()),
          ...Array.from(digerByTesis.keys()),
        ]);

        const rowsArr = Array.from(tesisSet)
          .sort((a, b) => a.localeCompare(b, "tr"))
          .map((tesis) => {
            const pompalar = pompaByTesis.get(tesis) || 0;
            const mikserler = mikserByTesis.get(tesis) || 0;
            const diger = digerByTesis.get(tesis) || 0;
            return { tesis, pompalar, mikserler, diger, toplam: pompalar + mikserler + diger };
          });

        const totals = rowsArr.reduce(
          (acc, r) => ({
            pompalar: acc.pompalar + r.pompalar,
            mikserler: acc.mikserler + r.mikserler,
            diger: acc.diger + r.diger,
            toplam: acc.toplam + r.toplam,
          }),
          { pompalar: 0, mikserler: 0, diger: 0, toplam: 0 }
        );

        setOzetRows(rowsArr);
        setOzetTotals(totals);
      } catch (e) {
        setOzetRows([]);
        setOzetTotals({ pompalar: 0, mikserler: 0, diger: 0, toplam: 0 });
      }


      // init group defaults for new groups only
      setGroupState((prev) => {
        const next = { ...prev };
        for (const r of list) {
          const gk = `${normKey(r.pompaPlaka)}__${r.santiyeAdi}`;
          if (!next[gk]) {
            next[gk] = {
              baslangicSaat: asText(r.baslangicSaat) || "",
              bitisSaat: asText(r.bitisSaat) || "",
              operatorList: r.operatorList?.length ? r.operatorList : [""],
              yardimciList: r.yardimciList?.length ? r.yardimciList : [""],
            };
          }
        }
        return next;
      });

      // init rowState for new docs only
      setRowState((prev) => {
        const next = { ...prev };
        for (const r of list) {
          if (!next[r._id]) {
            next[r._id] = {
              baslangicSaat: asText(r.baslangicSaat) || "",
              bitisSaat: asText(r.bitisSaat) || "",
              fiiliM3: r.fiiliM3 ?? "",
              operatorList: r.operatorList?.length ? r.operatorList : [""],
              yardimciList: r.yardimciList?.length ? r.yardimciList : [""],
              _ov: {},
            };
          }
        }
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  // Özet tablo yüksekliğine göre grid'i aşağı kaydır
  useEffect(() => {
    const el = ozetRef.current;
    const h = el ? Math.ceil(el.getBoundingClientRect().height || 0) : 0;
    setOzetHeight(h);
  }, [ozetRows]);



  // initial fetch
  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, []);

  /* -------- GROUPING (pompaPlaka + santiye) -------- */

  const groups = useMemo(() => {
    const map = new Map();
    for (const r of docs) {
      const gk = `${normKey(r.pompaPlaka)}__${r.santiyeAdi}`;
      if (!map.has(gk)) {
        map.set(gk, {
          groupKey: gk,
          pompaPlaka: r.pompaPlaka,
          santiyeAdi: r.santiyeAdi,
          musteriAdi: r.musteriAdi,
          rows: [],
        });
      }
      map.get(gk).rows.push(r);
    }

    const arr = [...map.values()].sort((a, b) => {
      const ap = asText(a.pompaPlaka).localeCompare(asText(b.pompaPlaka), "tr");
      if (ap !== 0) return ap;
      return asText(a.santiyeAdi).localeCompare(asText(b.santiyeAdi), "tr");
    });

    // stable child order by irsaliyeNo
    arr.forEach((g) => {
      g.rows.sort((x, y) => asText(x.irsaliyeNo).localeCompare(asText(y.irsaliyeNo), "tr"));
    });

    return arr;
  }, [docs]);

  useEffect(() => {
    const isTypingTarget = (target) => {
      const tag = String(target?.tagName || "").toLowerCase();
      const type = String(target?.type || "").toLowerCase();
      return (tag === "input" && type !== "date") || tag === "textarea" || tag === "select" || target?.isContentEditable;
    };
    const onKeyDown = (e) => {
      if (e.code === "Space" && !isTypingTarget(e.target)) {
        spaceShortcutRef.current = true;
        return;
      }
      if (e.key !== "Enter" || isTypingTarget(e.target)) return;
      e.preventDefault();
      if (spaceShortcutRef.current) handleSave();
      else fetchRows();
    };
    const onKeyUp = (e) => {
      if (e.code === "Space") spaceShortcutRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line
  }, [loading, saving, filterDate, groups, groupState, rowState]);

  /* -------- FILTERS (group level) -------- */

  const groupFilterValues = (key) => {
    if (key === "pompaPlaka") return uniq(groups.map((g) => asText(g.pompaPlaka)));
    if (key === "musteriAdi") return uniq(groups.map((g) => asText(g.musteriAdi)));
    if (key === "santiyeAdi") return uniq(groups.map((g) => asText(g.santiyeAdi)));
    return [];
  };

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const cur = prev[key] || [];
      return {
        ...prev,
        [key]: cur.includes(value)
          ? cur.filter((v) => v !== value)
          : [...cur, value],
      };
    });
  };

  const openFilter = (key, e) => {
    setOpenFilterKey(key);
    setFilterAnchor(e.currentTarget.getBoundingClientRect());
  };

  const filteredGroups = useMemo(() => {
    return groups.filter((g) =>
      Object.entries(filters).every(([key, values]) => {
        if (!values || values.length === 0) return true;
        const v =
          key === "pompaPlaka"
            ? asText(g.pompaPlaka)
            : key === "musteriAdi"
            ? asText(g.musteriAdi)
            : key === "santiyeAdi"
            ? asText(g.santiyeAdi)
            : "";
        return values.includes(v);
      })
    );
  }, [groups, filters]);

  /* -------- EFFECTIVE VALUES (group defaults + row override) -------- */

  const getEffectiveForRow = (row, gk) => {
    const gs = groupState[gk] || {};
    const rs = rowState[row._id] || { _ov: {} };
    const ov = rs._ov || {};

    const baslangicSaat = ov.baslangicSaat ? rs.baslangicSaat : (gs.baslangicSaat || "");
    const bitisSaat = ov.bitisSaat ? rs.bitisSaat : (gs.bitisSaat || "");

    const operatorList = ov.operatorList ? rs.operatorList : (gs.operatorList || [""]);
    const yardimciList = ov.yardimciList ? rs.yardimciList : (gs.yardimciList || [""]);

    let fiiliM3 = rs.fiiliM3;
    if (asText(fiiliM3) === "") fiiliM3 = row.gelenM3;

    return { baslangicSaat, bitisSaat, operatorList, yardimciList, fiiliM3 };
  };

  /* -------- UPDATE GROUP -> APPLY TO CHILDREN (if not overridden) -------- */

  const updateGroupField = (gk, key, value) => {
    setGroupState((prev) => ({
      ...prev,
      [gk]: { ...(prev[gk] || {}), [key]: value },
    }));

    // sync children only if not overridden
    setRowState((prev) => {
      const next = { ...prev };
      const g = groups.find((x) => x.groupKey === gk);
      if (!g) return prev;

      for (const r of g.rows) {
        const cur = next[r._id] || { _ov: {} };
        const ov = cur._ov || {};
        if (ov[key]) continue;
        next[r._id] = { ...cur, [key]: value, _ov: ov };
      }
      return next;
    });
  };

  const updateGroupList = (gk, key, list) => updateGroupField(gk, key, list);

  /* -------- UPDATE CHILD (set override) -------- */

  const updateChildField = (rowId, key, value) => {
    setRowState((prev) => {
      const cur = prev[rowId] || { _ov: {} };
      const ov = { ...(cur._ov || {}) };

      const val =
        key === "baslangicSaat" || key === "bitisSaat"
          ? formatTimeSmart(value)
          : value;

      ov[key] = true;
      return { ...prev, [rowId]: { ...cur, [key]: val, _ov: ov } };
    });
  };

  const updateChildList = (rowId, key, list) => {
    setRowState((prev) => {
      const cur = prev[rowId] || { _ov: {} };
      const ov = { ...(cur._ov || {}) };
      ov[key] = true;
      return { ...prev, [rowId]: { ...cur, [key]: list, _ov: ov } };
    });
  };

  /* -------- EXPAND/COLLAPSE -------- */

  const toggleExpand = (gk) => setExpanded((p) => ({ ...p, [gk]: !p[gk] }));

  /* -------- CHAIN COPY (within group, like CarsafSevkiyat driver copy) -------- */

  const propagateFromPrevInGroup = (gk, rowId, fieldKey) => {
    const g = groups.find((x) => x.groupKey === gk);
    if (!g) return;

    const idx = g.rows.findIndex((r) => r._id === rowId);
    if (idx <= 0) return;

    const cur = rowState[rowId];
    const curVal = (cur?.[fieldKey] || [""]).map(asText).filter(Boolean);
    if (curVal.length > 0) return;

    for (let k = idx - 1; k >= 0; k--) {
      const prevId = g.rows[k]._id;
      const prev = rowState[prevId];
      const prevVal = (prev?.[fieldKey] || [""]).map(asText).filter(Boolean);
      if (prevVal.length > 0) {
        setRowState((p) => {
          const nxt = { ...p };
          const c = nxt[rowId] || { _ov: {} };
          // override set etmiyoruz; user değiştirirse override olur
          nxt[rowId] = { ...c, [fieldKey]: prevVal, _ov: c._ov || {} };
          return nxt;
        });
        break;
      }
    }
  };

  /* -------- VALIDATION -------- */

  const validateAll = () => {
    const badDocs = [];
    const badGroupsSet = new Set();

    const firstFilled = (arr) => asText(arr?.[0]) !== "";

    for (const g of groups) {
      const gk = g.groupKey;
      const gs = groupState[gk] || {};
      const groupComplete = g.rows.every((r) => r.tamamlandi === true);

      // Group level warnings (because child will inherit)
      if (!groupComplete) {
        if (!firstFilled(gs.operatorList)) badGroupsSet.add(gk);
        if (parseHHMM(gs.baslangicSaat) == null) badGroupsSet.add(gk);
        if (parseHHMM(gs.bitisSaat) == null) badGroupsSet.add(gk);
      }

      for (const r of g.rows) {
        if (r.tamamlandi === true) continue;
        const eff = getEffectiveForRow(r, gk);

        const errs = [];
        if (!firstFilled(eff.operatorList)) errs.push("Operatör boş");
        if (parseHHMM(eff.baslangicSaat) == null) errs.push("Baş. saat boş/hatalı");
        if (parseHHMM(eff.bitisSaat) == null) errs.push("Bit. saat boş/hatalı");

        const fm3 = safeNum(eff.fiiliM3);
        if (fm3 == null || fm3 <= 0) errs.push("Fiili m3 boş/hatalı");

        if (errs.length) {
          badDocs.push(r._id);
          badGroupsSet.add(gk);
        }
      }
    }

    setBadDocIds(badDocs);
    setBadGroupKeys([...badGroupsSet]);
    return { badDocs, badGroups: [...badGroupsSet] };
  };

  useEffect(() => {
    validateAll();
    // eslint-disable-next-line
  }, [groups, groupState, rowState]);

  /* -------- SAVE -------- */

  const handleSave = async () => {
    if (saving) return;
    const email = auth.currentUser?.email || "";

    const { badDocs } = validateAll();
    if (badDocs.length > 0) {
      const ok = window.confirm("Bazı satırlar hatalı/eksik. Yine de kaydedilsin mi?");
      if (!ok) return;
    }

    setSaving(true);
    setLoading(true);
    try {
      for (const g of groups) {
        const gk = g.groupKey;

        for (const r of g.rows) {
          if (r.tamamlandi === true) continue;
          const eff = getEffectiveForRow(r, gk);

          const op = listToFirestoreFields3(eff.operatorList);
          const yg = listToFirestoreFields3(eff.yardimciList);

          const _fm3 = safeNum(eff.fiiliM3);
          const _basOK = parseHHMM(eff.baslangicSaat) != null;
          const _bitOK = parseHHMM(eff.bitisSaat) != null;
          const _opOK = eff.operatorList && asText(eff.operatorList[0]) !== "";
          const _ygOK = eff.yardimciList && asText(eff.yardimciList[0]) !== "";
          const isComplete = _fm3 != null && _fm3 > 0 && _basOK && _bitOK && _opOK && _ygOK;

          await updateDoc(doc(db, "hazirbeton_pompa_hakedis", r._id), {
            baslangicSaat: eff.baslangicSaat || "",
            bitisSaat: eff.bitisSaat || "",
            fiiliM3: safeNum(eff.fiiliM3),

            operatorAdi: op.v1,
            operatorAdi2: op.v2,
            operatorAdi3: op.v3,

            yagciAdi: yg.v1,
            yagciAdi2: yg.v2,
            yagciAdi3: yg.v3,

            tamamlandi: isComplete,
            updatedAt: serverTimestamp(),
            updatedBy: email,
          });
        }
      }

      alert("Kaydedildi");
      await releaseActiveGroupLock();
      fetchRows();
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  /* ================= UI ================= */

const ozetTh = {
  border: "1px solid #e0e0e0",
  padding: "6px 6px",
  background: "#fafafa",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const ozetTd = {
  border: "1px solid #e0e0e0",
  padding: "6px 6px",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const hbButtonStyle = (variant = "secondary", disabled = false) => ({
  border: "1px solid " + (variant === "primary" ? "#1d4ed8" : "#cbd5e1"),
  borderRadius: 7,
  padding: "7px 14px",
  fontWeight: 700,
  color: disabled ? "#94a3b8" : variant === "primary" ? "#fff" : "#0f172a",
  background: disabled
    ? "#f1f5f9"
    : variant === "primary"
      ? "linear-gradient(180deg, #3b82f6, #2563eb)"
      : "linear-gradient(180deg, #fff, #f8fafc)",
  boxShadow: disabled ? "none" : "0 1px 2px rgba(15,23,42,0.12)",
  cursor: disabled ? "not-allowed" : "pointer",
});

  const headers = [
    { key: "pompaPlaka", label: "Pompa Plaka", filterable: true },
    { key: "musteriAdi", label: "Müşteri", filterable: true },
    { key: "santiyeAdi", label: "Şantiye", filterable: true },
    { key: "gelenToplam", label: "Gelen m3 (Toplam)", filterable: false },
    { key: "fiiliToplam", label: "Fiili m3 (Toplam)", filterable: false },
    { key: "baslangicSaat", label: "Baş. Saat", filterable: false },
    { key: "bitisSaat", label: "Bit. Saat", filterable: false },
    { key: "sure", label: "Süre", filterable: false },
    { key: "operatorList", label: "Operatör", filterable: false },
    { key: "yardimciList", label: "Yardımcı", filterable: false },
  ];

  const childHeaders = [
    { key: "irsaliyeNo", label: "İrsaliye" },
    { key: "mikserPlaka", label: "Mikser" },
    { key: "gelenM3", label: "Gelen m3" },
    { key: "fiiliM3", label: "Fiili m3" },
    { key: "baslangicSaat", label: "Baş. Saat" },
    { key: "bitisSaat", label: "Bit. Saat" },
    { key: "sure", label: "Süre" },
    { key: "operatorList", label: "Operatör" },
    { key: "yardimciList", label: "Yardımcı" },
  ];

  return (
    <div style={{ padding: 16, position: "relative" }}>
      <h2>Pompa Hakediş (Gruplu)</h2>

      <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
        <button onClick={fetchRows} disabled={loading} style={hbButtonStyle("secondary", loading)} title="Enter">
          Getir
        </button>
        <button onClick={handleSave} disabled={loading || saving} style={hbButtonStyle("primary", loading || saving)} title="Space + Enter">
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>



      {/* Sağ üst özet tablo 
     <div
        ref={ozetRef}
        style={{
          position: "absolute",
          top: 56,
          right: 12,
          background: "white",
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          fontSize: 12,
          maxWidth: "calc(100% - 24px)",
          overflowX: "auto",
        }}
      > 
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Tesis Bazlı Özet</div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={ozetTh}></th>
              {ozetRows.map((r) => (
                <th key={r.tesis} style={ozetTh}>
                  {r.tesis}
                </th>
              ))}
              <th style={ozetTh}>TOPLAM</th>
            </tr>
          </thead>

          <tbody>

            <tr>
              <td style={{ ...ozetTd, fontWeight: 700, textAlign: "left" }}>Mikserlerimiz</td>
              {ozetRows.map((r) => (
                <td key={r.tesis} style={ozetTd}>
                  {fmtTr(r.mikserler)}
                </td>
              ))}
              <td style={{ ...ozetTd, fontWeight: 700 }}>{fmtTr(ozetTotals.mikserler)}</td>
            </tr>

            <tr>
              <td style={{ ...ozetTd, fontWeight: 700, textAlign: "left" }}>Pompalarımız</td>
              {ozetRows.map((r) => (
                <td key={r.tesis} style={ozetTd}>
                  {fmtTr(r.pompalar)}
                </td>
              ))}
              <td style={{ ...ozetTd, fontWeight: 700 }}>{fmtTr(ozetTotals.pompalar)}</td>
            </tr>
            
            <tr>
              <td style={{ ...ozetTd, fontWeight: 700, textAlign: "left" }}>Diğer</td>
              {ozetRows.map((r) => (
                <td key={r.tesis} style={ozetTd}>
                  {fmtTr(r.diger)}
                </td>
              ))}
              <td style={{ ...ozetTd, fontWeight: 700 }}>{fmtTr(ozetTotals.diger)}</td>
            </tr>

            <tr style={{ background: "#f6f6f6", fontWeight: 800 }}>
              <td style={{ ...ozetTd, textAlign: "left" }}>GENEL TOPLAM</td>
              {ozetRows.map((r) => (
                <td key={r.tesis} style={ozetTd}>
                  {fmtTr(r.toplam)}
                </td>
              ))}
              <td style={{ ...ozetTd, fontWeight: 800 }}>{fmtTr(ozetTotals.toplam)}</td>
            </tr>
          </tbody>
        </table>
      </div>
*/}
      {/* Grid sadece özet tablo kadar aşağıdan başlasın */}
      <div style={{ marginTop: ozetHeight ? ozetHeight + 16 : 0 }}> 

      <table style={{ width: "100%", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}></th>
            {headers.map((h) => (
              <th key={h.key} style={thStyle}>
                {h.label}{" "}
                {h.filterable && (
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={(e) => openFilter(h.key, e)}
                    title="Filtre"
                  >
                    ▼
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredGroups.map((g) => {
            const gk = g.groupKey;
            const groupComplete = g.rows.every((r) => r.tamamlandi === true);
            const gs = groupState[gk] || {
              baslangicSaat: "",
              bitisSaat: "",
              operatorList: [""],
              yardimciList: [""],
            };

            const gelenToplam = g.rows.reduce((a, r) => a + (safeNum(r.gelenM3) ?? 0), 0);
            const fiiliToplam = g.rows.reduce((a, r) => {
              const eff = getEffectiveForRow(r, gk);
              return a + (safeNum(eff.fiiliM3) ?? 0);
            }, 0);

            const groupBad = badGroupKeys.includes(gk);

            return (
              <React.Fragment key={gk}>
                {/* GROUP ROW */}
                <tr
                  style={{
                    background: groupComplete ? "#f0f0f0" : groupBad ? "#ffe1e1" : undefined,
                    opacity: isLockedByOther(gk) ? 0.55 : 1,
                    color: groupComplete ? "#666" : undefined,
                    cursor: groupComplete ? "default" : "pointer",
                  }}
                  title={lockTooltip(gk)}
                  onClick={async () => {
                    if (groupComplete) return;
                    if (isLockedByOther(gk)) {
                      alert(lockTooltip(gk) || "Bu grup kilitli.");
                      return;
                    }
                    await acquireGroupLock(gk);
                  }}
                >
                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => toggleExpand(gk)}
                      style={{ width: 26, height: 22, cursor: "pointer", fontWeight: 700 }}
                      title="Grubu aç/kapat"
                    >
                      {expanded[gk] ? "−" : "+"}
                    </button>
                  </td>

                  <td style={tdStyle}>{g.pompaPlaka}</td>
                  <td style={tdStyle}>{g.musteriAdi}</td>
                  <td style={tdStyle}>{g.santiyeAdi}</td>
                  <td style={tdStyle}><b>{gelenToplam}</b></td>
                  <td style={tdStyle}><b>{fiiliToplam}</b></td>

                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={gs.baslangicSaat}
                      disabled={groupComplete || isLockedByOther(gk)}
                      placeholder="HH:MM"
                      onChange={(e) =>
                        updateGroupField(gk, "baslangicSaat", formatTimeSmart(e.target.value))
                      }
                    />
                  </td>

                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={gs.bitisSaat}
                      disabled={groupComplete || isLockedByOther(gk)}
                      placeholder="HH:MM"
                      onChange={(e) =>
                        updateGroupField(gk, "bitisSaat", formatTimeSmart(e.target.value))
                      }
                    />
                  </td>

                  <td style={tdStyle}>
                    <b>{calcSure(gs.baslangicSaat, gs.bitisSaat)}</b>
                  </td>

                  <td style={tdStyle}>
                    <DynamicListCell
                      values={gs.operatorList}
                      datalistId="dl-personeller"
                      placeholder="Operatör"
                      addLabel="+ Operatör"
                      disabled={groupComplete || isLockedByOther(gk)}
                      onChange={(list) => updateGroupList(gk, "operatorList", list)}
                    />
                  </td>

                  <td style={tdStyle}>
                    <DynamicListCell
                      values={gs.yardimciList}
                      datalistId="dl-personeller"
                      placeholder="Yardımcı"
                      addLabel="+ Yardımcı"
                      disabled={groupComplete || isLockedByOther(gk)}
                      onChange={(list) => updateGroupList(gk, "yardimciList", list)}
                    />
                  </td>
                </tr>

                {/* CHILD TABLE */}
                {expanded[gk] && (
                  <tr>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, padding: 0 }} colSpan={headers.length}>
                      <table style={{ width: "100%", fontSize: 12 }}>
                        <thead>
                          <tr>
                            {childHeaders.map((h) => (
                              <th key={h.key} style={{ ...thStyle, background: "#fafafa" }}>
                                {h.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r) => {
                            const rs = rowState[r._id] || { _ov: {} };
                            const eff = getEffectiveForRow(r, gk);
                            const isBad = badDocIds.includes(r._id);
                            const rowComplete = r.tamamlandi === true;

                            return (
                              <tr
                                key={r._id}
                                style={{
                                  background: rowComplete ? "#f0f0f0" : isBad ? "#ffe1e1" : undefined,
                                  opacity: isLockedByOther(gk) ? 0.55 : 1,
                                  color: rowComplete ? "#666" : undefined,
                                  cursor: rowComplete ? "default" : "pointer",
                                }}
                                title={lockTooltip(gk)}
                                onClick={async () => {
                                  if (rowComplete) return;
                                  if (isLockedByOther(gk)) {
                                    alert(lockTooltip(gk) || "Bu grup kilitli.");
                                    return;
                                  }
                                  await acquireGroupLock(gk);
                                }}
                              >
                                <td style={tdStyle}>{r.irsaliyeNo}</td>
                                <td style={tdStyle}>{r.mikserPlaka}</td>
                                <td style={tdStyle}>{r.gelenM3}</td>

                                <td style={tdStyle}>
                                  <input
                                    type="text"
                                    value={rs.fiiliM3 ?? ""}
                                    disabled={rowComplete || isLockedByOther(gk)}
                                    placeholder={String(r.gelenM3 ?? "")}
                                    onChange={(e) => updateChildField(r._id, "fiiliM3", e.target.value)}
                                  />
                                </td>

                                <td style={tdStyle}>
                                  <input
                                    type="text"
                                    value={rs.baslangicSaat ?? ""}
                                    disabled={rowComplete || isLockedByOther(gk)}
                                    placeholder={gs.baslangicSaat || "HH:MM"}
                                    onChange={(e) => updateChildField(r._id, "baslangicSaat", e.target.value)}
                                  />
                                </td>

                                <td style={tdStyle}>
                                  <input
                                    type="text"
                                    value={rs.bitisSaat ?? ""}
                                    disabled={rowComplete || isLockedByOther(gk)}
                                    placeholder={gs.bitisSaat || "HH:MM"}
                                    onChange={(e) => updateChildField(r._id, "bitisSaat", e.target.value)}
                                  />
                                </td>

                                <td style={tdStyle}>
                                  <b>{calcSure(eff.baslangicSaat, eff.bitisSaat)}</b>
                                </td>

                                <td style={tdStyle}>
                                  <DynamicListCell
                                    values={rs.operatorList}
                                    datalistId="dl-personeller"
                                    placeholder="Operatör"
                                    addLabel="+"
                                    disabled={rowComplete || isLockedByOther(gk)}
                                    onFocusFirst={() =>
                                      propagateFromPrevInGroup(gk, r._id, "operatorList")
                                    }
                                    onChange={(list) => updateChildList(r._id, "operatorList", list)}
                                  />
                                </td>

                                <td style={tdStyle}>
                                  <DynamicListCell
                                    values={rs.yardimciList}
                                    datalistId="dl-personeller"
                                    placeholder="Yardımcı"
                                    addLabel="+"
                                    disabled={rowComplete || isLockedByOther(gk)}
                                    onFocusFirst={() =>
                                      propagateFromPrevInGroup(gk, r._id, "yardimciList")
                                    }
                                    onChange={(list) => updateChildList(r._id, "yardimciList", list)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* DATALIST - Tüm personeller */}
      <datalist id="dl-personeller">
        {personeller.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>

      {/* FILTER POPUP */}
      {openFilterKey && (
        <FilterPopup
          anchor={filterAnchor}
          values={groupFilterValues(openFilterKey)}
          selected={filters[openFilterKey] || []}
          onToggle={(v) => toggleFilter(openFilterKey, v)}
          onSelectAll={() =>
            setFilters((prev) => ({
              ...prev,
              [openFilterKey]: groupFilterValues(openFilterKey),
            }))
          }
          onClear={() =>
            setFilters((prev) => ({
              ...prev,
              [openFilterKey]: [],
            }))
          }
          onClose={() => setOpenFilterKey(null)}
        />
      )}

      {/* SUMMARY */}
      <div style={{ marginTop: 16 }}>
        <h3 style={{ margin: "8px 0" }}>Plaka Bazında Kontrol</h3>
        {(() => {
          // Her satır için bom bilgisini ekle
          const rows = filteredGroups.map((g) => {
            const gk = g.groupKey;
            const gelen = g.rows.reduce((a, r) => a + (safeNum(r.gelenM3) ?? 0), 0);
            const fiili = g.rows.reduce((a, r) => a + (safeNum(getEffectiveForRow(r, gk).fiiliM3) ?? 0), 0);
            const bom = bomMap[normKey(g.pompaPlaka)] ?? "";
            const bomLabel = bom !== "" ? `${bom}m` : "—";
            return { ...g, gelen, fiili, bom, bomLabel };
          });

          // Bom'a göre gruplandır
          const bomGroups = [];
          const bomSeen = new Map();
          rows.forEach((r) => {
            const key = r.bom !== "" ? String(r.bom) : "—";
            if (!bomSeen.has(key)) {
              bomSeen.set(key, bomGroups.length);
              bomGroups.push({ bom: key, label: r.bomLabel, rows: [] });
            }
            bomGroups[bomSeen.get(key)].rows.push(r);
          });

          // Bom'a göre sırala (sayısal, — en sona)
          bomGroups.sort((a, b) => {
            if (a.bom === "—") return 1;
            if (b.bom === "—") return -1;
            return Number(a.bom) - Number(b.bom);
          });

          // Genel toplam
          const genelGelen = rows.reduce((a, r) => a + r.gelen, 0);
          const genelFiili = rows.reduce((a, r) => a + r.fiili, 0);

          return (
            <table style={{ width: "70%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Bom</th>
                  <th style={thStyle}>Pompa Plaka</th>
                  <th style={thStyle}>Şantiye</th>
                  <th style={thStyle}>Gelen m³</th>
                  <th style={thStyle}>Fiili m³</th>
                </tr>
              </thead>
              <tbody>
                {bomGroups.map((bg) => {
                  const bgGelen = bg.rows.reduce((a, r) => a + r.gelen, 0);
                  const bgFiili = bg.rows.reduce((a, r) => a + r.fiili, 0);
                  return (
                    <React.Fragment key={"bg_" + bg.bom}>
                      {bg.rows.map((g, idx) => (
                        <tr key={"sum_" + g.groupKey}>
                          {/* Bom sütunu sadece ilk satırda, rowspan ile */}
                          {idx === 0 && (
                            <td
                              style={{ ...tdStyle, fontWeight: "bold", background: "#f0f4ff", verticalAlign: "middle", textAlign: "center" }}
                              rowSpan={bg.rows.length}
                            >
                              {bg.label}
                            </td>
                          )}
                          <td style={tdStyle}>{g.pompaPlaka}</td>
                          <td style={tdStyle}>{g.santiyeAdi}</td>
                          <td style={tdStyle}><b>{g.gelen}</b></td>
                          <td style={tdStyle}><b>{g.fiili}</b></td>
                        </tr>
                      ))}
                      {/* Bom grubu ara toplamı */}
                      <tr style={{ background: "#e8f0fe" }}>
                        <td style={{ ...tdStyle, fontWeight: "bold", textAlign: "right" }} colSpan={3}>
                          {bg.label} Ara Toplam
                        </td>
                        <td style={{ ...tdStyle, fontWeight: "bold" }}>{bgGelen}</td>
                        <td style={{ ...tdStyle, fontWeight: "bold" }}>{bgFiili}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {/* Genel toplam */}
                <tr style={{ background: "#1a237e", color: "#fff" }}>
                  <td style={{ ...tdStyle, fontWeight: "bold", border: "1px solid #3949ab", textAlign: "right" }} colSpan={3}>
                    GENEL TOPLAM
                  </td>
                  <td style={{ ...tdStyle, fontWeight: "bold", border: "1px solid #3949ab" }}>{genelGelen}</td>
                  <td style={{ ...tdStyle, fontWeight: "bold", border: "1px solid #3949ab" }}>{genelFiili}</td>
                </tr>
              </tbody>
            </table>
          );
        })()}
      </div>
      </div>

    </div>
  );
}

/* ================= STYLES ================= */





const thStyle = {
  border: "1px solid #ccc",
  padding: 6,
  background: "#f3f3f3",
  textAlign: "left",
};

const tdStyle = {
  border: "1px solid #e0e0e0",
  padding: 4,
  verticalAlign: "top",
};
